<template>
  <article class="round-menu column gap-4">
    <section class="label mt-0 line justify-between">
      <div>
        <div v-if="roundPlayed">
          <span>{{ $t("messages.roundSimulated") }} </span>
          <lucide-icon name="check" class="ml-2" />
        </div>
        <div v-else-if="isCurrentRound">
          <span>{{ $t("messages.isCurrentRound") }} </span>
        </div>
        <div v-else>
          <span>{{ $t("messages.openRound") }} </span>
        </div>
      </div>
      <div class="line gap-3">
        <button
          class="button-icon p-0 text-18"
          type="button"
          @click="inspectSimulationRound"
        >
          <lucide-icon name="sigma" />
        </button>
        <documentation-anchor
          :update-store="false"
          :object="roundActionPlan"
          prop="notes"
          style="margin-right: 0.5rem"
          @update:value="onNotesUpdate($event)"
        />
      </div>
    </section>

    <!-- INCIDENT LIST -->
    <section v-if="features.incident && roundIncidents.length > 0">
      <div class="label">
        <lucide-icon name="incident" class="round-menu__actionicon" />
        <span>{{ $t("objectNames.incident", 3) }}</span>
      </div>
      <transition-group name="transition-list" tag="div" class="list">
        <button-item
          v-for="incident in roundIncidents"
          :key="'inc_' + incident.id"
          :selected="
            openQuickInfos.some(
              (i) =>
                i.active &&
                i.itemType === 'incident' &&
                i.itemId === incident.id &&
                i.itemRoundIndex === roundIndex
            )
          "
          @click="onIncidentClick($event, incident)"
        >
          <span v-if="incident.title">
            {{ incident.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>

          <template #icons>
            <button
              type="button"
              class="button-icon button-item__icon text-16"
              @click="onInfoButtonClick($event, incident)"
            >
              <lucide-icon name="info" />
            </button>
          </template>
        </button-item>
      </transition-group>
    </section>

    <!-- PENDING ACTION EFFECTS LIST -->
    <section v-if="pendingEffects.length > 0">
      <button
        class="label round-menu__pendingeffectstitle"
        type="button"
        @click="pendingEffectsVisible = !pendingEffectsVisible"
      >
        <span>{{ $t("labels.pendingEffects", pendingEffects.length) }}</span>

        <lucide-icon
          :name="pendingEffectsVisible ? 'chevron-up' : 'chevron-down'"
        />
      </button>
      <transition name="drawer">
        <div v-if="pendingEffectsVisible" class="list pv-1">
          <store-item
            v-for="pendingEffectItem of pendingEffects"
            :id="pendingEffectItem.id"
            :key="`${pendingEffectItem.roundIndex}${pendingEffectItem.type}${pendingEffectItem.id}`"
            v-slot="{ item }"
            :type="pendingEffectItem.type"
          >
            <button-item
              :selected="
                openQuickInfos.some(
                  (i) =>
                    i.active &&
                    i.itemType === item.type &&
                    i.itemId === item.id &&
                    i.itemRoundIndex === pendingEffectItem.roundIndex
                )
              "
              @click="
                inspectSimulationItem(
                  $event,
                  'pendingEffect',
                  pendingEffectItem,
                  pendingEffectItem.roundIndex
                )
              "
            >
              <div>{{ item.title }}</div>

              <div class="round-menu__pendingeffectdate">
                <template v-if="item.type == 'incident'">
                  {{ $t("labels.activatedIn") }}
                </template>
                <template v-else>
                  {{ $t("labels.playedIn") }}
                </template>
                {{ formatRoundIndex(pendingEffectItem.roundIndex) }}
              </div>

              <template #icons>
                <button
                  type="button"
                  class="button-icon button-item__icon text-16"
                  @click="onInfoButtonClick($event, item)"
                >
                  <lucide-icon name="info" />
                </button>
              </template>

              <!-- {{ pendingEffectItem.effects }} -->
            </button-item>
          </store-item>
        </div>
      </transition>
    </section>

    <!-- ACTION LIST -->
    <section
      v-if="
        features.action &&
        ((isInputAllowed && allActionsCount > 0) || roundActions.length > 0)
      "
    >
      <template
        v-if="allActionsCount > 0 && (!roundPlayed || roundActions.length > 0)"
      >
        <div class="label">
          <lucide-icon name="action" class="round-menu__actionicon" />
          <span>{{ $t("objectNames.action", 3) }}</span>
        </div>
        <sortable-list
          :items="roundActions"
          :order="roundActionPlan.plannedActionIds"
          :sortable="!roundPlayed"
          :group="{ id: 'simulation-actions', roundIndex: roundIndex }"
          @update:order="updateActionIds"
          @move-list="
            moveActionsToRound({
              actionIds: $event.items.map(i => i.id),
              roundIndex: $event.targetList.roundIndex,
              actionIndex: $event.targetIndex
            })
          "
        >
          <template #default="{ item: action }">
            <button-item
              :class="{
                'not-playable':
                  blockedActionIds.includes(action.id + '') ||
                  actionPlayableStates[action.id] === false
              }"
              :selected="
                openQuickInfos.some(
                  (i) =>
                    i.active &&
                    i.itemType === action.type &&
                    i.itemId === action.id &&
                    i.itemRoundIndex === roundIndex
                )
              "
              @click="onActionClick($event, action)"
            >
              <span v-if="action.title">
                {{ action.title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
              <template #icons>
                <button
                  type="button"
                  class="button-icon button-item__icon button-item__icon-darker text-16"
                  @click="onInfoButtonClick($event, action)"
                >
                  <lucide-icon name="info" />
                </button>
                <button
                  v-if="canRemoveAction(action.id)"
                  type="button"
                  class="button-icon button-item__icon button-item__icon-darker text-16 pl-0"
                  @click="onRemoveActionClick($event, action)"
                >
                  <lucide-icon name="x" />
                </button>
              </template>
            </button-item>
          </template>

          <template #after-list>
            <!-- ACTION INPUT -->
            <input-dropdown
              v-if="isInputAllowed && Object.keys(otherActions).length > 0"
              :key="`actioninput-${roundIndex}`"
              :options="otherActions"
              :order="actionsOrder"
              :must-confirm="true"
              :clear-on-select="true"
              :placeholder="'+ ' + $t('actions.addAction')"
              :searchable="true"
              @preselect="showActionInfo"
              @deselect="hideActionInfo"
              @select="addAction"
            >
              <template #default="{ item: action }">
                <span
                  :class="{
                    'option_not-playable':
                      actionPayableStates[action.id] === false ||
                      actionUsesLeftStates[action.id] === false ||
                      actionRequirementStates[action.id] === false
                  }"
                  :title="getActionNotPlayableReason(action)"
                >
                  <span v-if="action.title">
                    {{ action.title }}
                  </span>
                  <span v-else class="placeholder-text">
                    {{ $t("labels.unnamed") }}
                  </span>
                </span>
              </template>
            </input-dropdown>
          </template>
        </sortable-list>
      </template>
    </section>
    <section
      v-else-if="
        isInputAllowed && simulation?.scenarioId != null && allActionsCount == 0
      "
    >
      {{ $t("errors.noActionsAvailable") }}
    </section>

    <section
      v-if="
        features.scheduledEffect &&
        simulation?.useScheduledEffects &&
        (roundActionPlan.scheduledEffects.length > 0 || isInputAllowed)
      "
    >
      <span class="label">
        <lucide-icon name="impulse" class="round-menu__actionicon" />
        <span>
          {{ $t("objectNames.impulse", 2) }}
        </span>
      </span>
      <transition-group-list tag="div" class="list mb-2">
        <scheduled-effect
          v-for="effect in roundActionPlan.scheduledEffects"
          :key="effect.id"
          ref="scheduledEffectElements"
          :effect="effect"
          :simulation-id="simulationId"
          :round-index="roundIndex"
          @update:effect="updateScheduledEffect($event)"
          @delete="removeScheduledEffect(effect.id)"
        ></scheduled-effect>

        <item-combo-select
          v-if="isInputAllowed && itemsDoExist"
          key="scheduledEffectInput"
          :targets="targets"
          :resources="resources"
          :indices="indices"
          :placeholder="$t('actions.createImpulse')"
          :types="['target', 'resource']"
          @select="onScheduledEffectItemsChoose"
        />
      </transition-group-list>
    </section>

    <div v-if="developerMode">
      <h5 @click="showImpulses = !showImpulses" class="label mt-0">
        {{ $t("objectNames.impulse", 2) }}
      </h5>
      <pre v-if="showImpulses">{{ roundImpulses }}</pre>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, useTemplateRef } from "vue";
import { pickBy, size } from "lodash-es";
import { useI18n } from "vue-i18n";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { findElementOrAncestor } from "@/helpers/utility/DOM.js";
import TransitionGroupList from "@core/wrappers/TransitionGroupList.vue";

import InputDropdown from "@core/input/InputDropdown.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import SortableList from "@core/ui/SortableList.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";

import StoreItem from "@editor/items/StoreItem.vue";

import ScheduledEffect from "@editor/items/scheduledEffect/ScheduledEffect.vue";

import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";
import { useRequirementStore } from "../requirement/requirements.store";
import { useConditionStore } from "../condition/conditions.store";
import { useFeatures } from "@editor/store/features.store";

import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";
import { getActionEffectsDuration } from "@editor/items/action/ActionFunctions";

import type { HandleableMouseEvent } from "@core/types/Events";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ScheduledEffectItem } from "@editor/items/scheduledEffect/ScheduledEffectItem";
import type { ConditionItem } from "../condition/ConditionItem";
import type { SimulationRoundItem } from "./SimulationRoundItem";
import { splitUid, type Uid } from "@/helpers/utility/Id";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { useStakeholderStore } from "../stakeholder/stakeholders.store";

const props = defineProps<{ simulationId: number; roundIndex: number }>();

const { t } = useI18n();
const features = useFeatures();
const simulationView = useSimulationViewStore();
const simulationStore = useSimulationStore();

const scheduledEffectElements = useTemplateRef("scheduledEffectElements");

const showImpulses = ref(false);
const choosingImpulseItems = ref(false);
const pendingEffectsVisible = ref(false);

const simulation = computed(() => simulationView.simulation);
const rounds = computed(() => simulationView.rounds);
const selectedRound = computed(() => simulationView.selectedRound);
const selectedRoundIndex = computed(() => simulationView.selectedRoundIndex);
const currentRoundIndex = computed(() => simulationView.currentRoundIndex);
const targets = computed(() => simulationView.targets);
const resources = computed(() => simulationView.resources);
const actions = computed(() => simulationView.actions);
const pendingEffectsByRound = computed(
  () => simulationView.pendingEffectsByRound
);
const requirementsStore = useRequirementStore();
const conditionStore = useConditionStore();

const round = computed(() => {
  return rounds.value[props.roundIndex];
});
const roundPlayed = computed(() => {
  if (props.roundIndex > currentRoundIndex.value) return false;
  if (simulation.value?.complete) return true;
  return props.roundIndex < currentRoundIndex.value;
});
const isCurrentRound = computed(() => {
  if (selectedRound.value == null) return false;
  return selectedRoundIndex.value == props.roundIndex;
});
const roundImpulses = computed(() => {
  return simulationView.impulses[props.roundIndex];
});
const pendingEffects = computed(() => {
  return pendingEffectsByRound.value[props.roundIndex];
});
const developerMode = computed(() => {
  return useSettingsStore().developerMode;
});
const isInputAllowed = computed(() => {
  if (simulation.value == null) return false;
  return (
    !roundPlayed.value &&
    !simulation.value.complete &&
    !simulationView.autoplayActive &&
    !simulationView.simulating &&
    !simulationView.visualizing
  );
});
const itemsDoExist = computed(() => {
  return size(targets.value) + size(resources.value) > 0;
});
const openQuickInfos = computed(() => {
  return simulationView.openQuickInfos?.[props.roundIndex] ?? [];
});
const indices = computed(() => useIndexStore().items);


// ===========================
// ACTIONS

const { loadedModel } = useLoadedModel();
const stakeholderStore = useStakeholderStore();
const modelStakeholder = computed(() => {
  if (loadedModel.stakeholderId == null) return null;
  return stakeholderStore.items[loadedModel.stakeholderId];
});

const roundActionPlan = computed(() => {
  return simulationStore.getActionPlan(props.simulationId, props.roundIndex);
});
const allActionsCount = computed(() => {
  return Object.values(actions.value).length;
});
/**
 * Unpicked actions that can still be added to the round.
 */
const otherActions = computed(() => {
  return pickBy(
    actions.value,
    (action) => {
      if (action == null) return false;
      if (modelStakeholder.value != null) {
        // if the model is bound to a stakeholder,
        // only show actions that belong to that stakeholder
        if (!modelStakeholder.value.actionIds.includes(action.id)) {
          return false;
        }
      }
      return !roundActionPlan.value.plannedActionIds.includes(action.id) &&
      // action is payable
      (!action.hiddenIfCostsNotCovered ||
        actionPayableStates.value[action.id] != false) &&
      // action was not already played enough times
      (!action.hiddenIfMaxUsesReached ||
        actionUsesLeftStates.value[action.id] != false) &&
      // action requirements are fulfilled
      (!action.hiddenIfRequirementsNotFulfilled ||
        actionRequirementStates.value[action.id] != false)
    }
  );
});
const actionsOrder = computed(() => {
  return useActionStore().order;
});
const roundActions = computed(() => {
  const plannedActionIds = roundActionPlan.value.plannedActionIds;
  return useActionStore().getArrayByIds(plannedActionIds);
});

const blockedActionIds = computed(() => {
  if (!roundPlayed.value) return [];
  if (!round.value) return [];
  return Object.keys(round.value.preventedActions).concat(
    Object.keys(round.value.notPayableActions)
  );
});
/**
 * @returns map of action ids to whether they are playable
 */
const actionPlayableStates = computed((): { [actionId: number]: boolean } => {
  if (props.roundIndex !== currentRoundIndex.value) return {};
  return simulationView.currentRoundActionsPlayable;
});
const actionRequirementStates = computed(
  (): { [actionId: number]: boolean } => {
    if (props.roundIndex == currentRoundIndex.value) {
      return simulationView.currentRoundActionsRequirementFulfilled;
    }

    // For future rounds, only check some requirements
    // (time-based conditions can be evaluated beforehand)

    if (round.value?.played) return {};

    const currentTimeRound: SimulationRoundItem = {
      type: "simulationRound",
      index: props.roundIndex, // <------ this is the important part
      played: round.value?.played || false,
      impulses: round.value?.impulses || {},
      startElementValues: {},
      elementValues: {},
      startRandomValues: {},
      randomValues: {},
      activatedActions: [],
      activatedIncidents: [],
      activatedMomentums: [],
      incidentCounts: {},
      actionCounts: {},
      triggerCounts: {},
      pendingEffects: [],
      preventedActions: [],
      notPayableActions: [],
      id: -1,
      uid: "simulationRound",
      createdAt: Date.now(),
    };

    return mapValues(simulationView.actions, (action: ActionItem) => {
      const requirements = requirementsStore.byItemUid[action.uid] || [];
      // no requirements, no fulfillment needed
      if (requirements.length == 0) return true;

      const requirementConditions = requirements.reduce((conditionsByRequirement, requirement) => {
        // ONLY get time-based conditions
        const timeBasedConditions = conditionStore.getBySource(requirement).filter(
          (condition) => (condition.activatorType == "round" && condition.comparedType == "value") || condition.activatorType == "roundRange"
        );
        conditionsByRequirement[requirement.id] = timeBasedConditions;
        return conditionsByRequirement;
      }, {} as { [requirementId: number]: ConditionItem[] });

      const conditionalRequirements = requirements.filter((requirement) => {
        return requirementConditions[requirement.id].length > 0;
      });
      // if there are no requirements, the action is always fulfilled
      if (conditionalRequirements.length == 0) return true;

      const fulfilled = conditionalRequirements.some((requirement) => {
        const conditions = requirementConditions[requirement.id];
        return conditions.every((condition) =>
          conditionStore.evaluate(condition.id, [currentTimeRound])
        );
      });
      return fulfilled;
    });
  }
);
const actionUsesLeftStates = computed((): { [actionId: number]: boolean } => {
  if (props.roundIndex !== currentRoundIndex.value) return {};
  return simulationView.currentRoundActionUsesLeftStates;
});
/**
 * @returns map of action ids to whether they are payable
 */
const actionPayableStates = computed((): { [actionId: number]: boolean } => {
  if (props.roundIndex !== currentRoundIndex.value) return {};
  return simulationView.currentRoundActionsPayable;
});

function canRemoveAction(actionId: number) {
  if (roundPlayed.value) return false;
  const action = actions.value[actionId];
  if (action == null) return false;
  if (modelStakeholder.value != null) {
    // if the model is bound to a stakeholder,
    // only allow removing actions that belong to that stakeholder
    if (!modelStakeholder.value.actionIds.includes(action.id)) {
      return false;
    }
  }
  return true;
}

function onRemoveActionClick(
  mouseEvent: HandleableMouseEvent,
  action: ActionItem
) {
  mouseEvent.hasBeenHandled = true;
  closeActionQuickInfo(action.id);
  simulationStore.removeAction({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    actionId: action.id
  });
}

function updateActionIds(plannedActionIds: number[]) {
  simulationStore.updateActionIds({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    actionIds: plannedActionIds
  });
}

function addAction({ item }: { item: ActionItem }) {
  const actionId = item.id;

  // prevent adding actions if the round is already complete
  if (roundPlayed.value) {
    return;
  }

  // we add the action to the list of actions
  simulationStore.addAction({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    actionId
  });
}

function moveActionsToRound({
  actionIds,
  roundIndex,
  actionIndex
}: {
  actionIds: number[];
  roundIndex: number;
  actionIndex: number;
}) {
  for (const actionId of actionIds) {
    closeActionQuickInfo(actionId);
  }
  return simulationStore.moveActions({
    simulationId: props.simulationId,
    roundIndexSource: props.roundIndex,
    roundIndexTarget: roundIndex,
    targetActionIndex: actionIndex,
    actionIds
  });
}

function closeActionQuickInfo(actionId: number) {
  simulationView.closeSimulationQuickInfo({
    itemType: "action",
    itemId: actionId,
    roundIndex: props.roundIndex,
    type: "action"
  });

  // close open pending effects popups
  const action = actions.value[actionId];
  const effectsDuration = getActionEffectsDuration(action.id);
  for (let i = 0; i < effectsDuration; i++) {
    simulationView.closeSimulationQuickInfo({
      itemType: "action",
      itemId: actionId,
      type: "pendingEffect",
      roundIndex: props.roundIndex + i,
      itemRoundIndex: props.roundIndex
    });
  }
}

function onActionClick(mouseEvent: HandleableMouseEvent, action: ActionItem) {
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  inspectSimulationItem(mouseEvent, "action", action);
}

function showActionInfo(action: { type: "action"; id: number }) {
  useOverlay().setItem(action);
}
function hideActionInfo() {
  useOverlay().hide();
}

function getActionNotPlayableReason(action: ActionItem) {
  let reasons = [];
  if (!action) return "";

  if (actionPayableStates.value[action.id] === false) {
    reasons.push(t("errors.actionNotPayable"));
  }
  if (actionUsesLeftStates.value[action.id] === false) {
    reasons.push(t("errors.actionMaxUsesReached"));
  }
  if (actionRequirementStates.value[action.id] === false) {
    reasons.push(t("errors.actionRequirementsNotFulfilled"));
  }

  return reasons.join("\n");
}

// ===========================
// INCIDENTS

const roundIncidents = computed(() => {
  if (!round.value) return [];
  const incidentIds = round.value.activatedIncidents.map(
    (incidentReason) => incidentReason.incidentId
  );
  return useIncidentStore()
    .getArrayByIds(incidentIds)
    .filter((incident) => incident.visibility !== "hidden");
});

function onIncidentClick(
  mouseEvent: HandleableMouseEvent,
  incident: IncidentItem
) {
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  inspectSimulationItem(mouseEvent, "incident", incident);
}

// ===========================
// SCHEDULED EFFECTS

/**
 * Create scheduled effects for all selected net items.
 * @param affectedItems
 */
function onScheduledEffectItemsChoose(
  affectedItems: { uid: Uid<"target" | "resource"> }[]
) {
  for (const item of affectedItems) {
    const { id, type } = splitUid(item.uid);
    addScheduledEffect({
      affectedId: id,
      affectedType: type,
      impulseExpressions: [""]
    });
  }
  choosingImpulseItems.value = false;

  // focus first of new impulses
  nextTick(() => {
    if (scheduledEffectElements.value == null) return;
    const index = scheduledEffectElements.value.length - affectedItems.length;
    if (index >= 0 && index < scheduledEffectElements.value.length) {
      scheduledEffectElements.value[index]?.focus();
    }
  });
}

/**
 * Add a new scheduled effect to the current round.
 */
function addScheduledEffect(
  scheduledEffect: Pick<
    ScheduledEffectItem,
    "affectedId" | "affectedType" | "impulseExpressions"
  >
) {
  simulationStore.addScheduledEffect({
    ...scheduledEffect,
    simulationId: props.simulationId,
    roundIndex: props.roundIndex
  });
}
function removeScheduledEffect(scheduledEffectId: string) {
  simulationStore.removeScheduledEffect({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    scheduledEffectId
  });
}
function updateScheduledEffect(scheduledEffect: ScheduledEffectItem) {
  simulationStore.updateScheduledEffect({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    effect: scheduledEffect
  });
}

// ===========================
// GENERAL

function inspectSimulationRound(mouseEvent: HandleableMouseEvent) {
  const targetElement = mouseEvent.target as HTMLElement;
  const targetBox = targetElement.getBoundingClientRect();
  simulationView.toggleSimulationInfo({
    x: targetBox.right,
    y: targetBox.top,
    type: "round",
    roundIndex: props.roundIndex
  });
}

/**
 * @param type - The type of occurrence to inspect
 * @param item - The item to inspect
 * @param roundIndexCreated - The round index the item occurred in the simulation
 */
function inspectSimulationItem(
  mouseEvent: HandleableMouseEvent,
  type: "action" | "pendingEffect" | "incident",
  item: { id: number; type: string },
  roundIndexCreated: null | number = null
) {
  const buttonElement = findElementOrAncestor(
    mouseEvent.target as HTMLElement,
    (el) => el.classList.contains("button-item")
  );
  if (buttonElement == null) {
    console.warn("no item clicked");
    return;
  }
  const targetBox = buttonElement.getBoundingClientRect();
  simulationView.toggleSimulationInfo({
    x: targetBox.right,
    y: targetBox.top,
    type,
    itemType: item.type,
    itemId: item.id,
    itemRoundIndex: roundIndexCreated ?? props.roundIndex,
    roundIndex: props.roundIndex
  });
}

function onInfoButtonClick(
  mouseEvent: HandleableMouseEvent,
  item: OverlayDialogItemRequest
) {
  mouseEvent.hasBeenHandled = true;
  useOverlay().setItem(item);
}


function onNotesUpdate(notes: string) {
  const docs = roundActionPlan.value.docs;
  simulationStore.updateRoundDocs({
    simulationId: props.simulationId,
    roundIndex: props.roundIndex,
    docs: { ...docs, notes }
  });
}

function formatRoundIndex(roundIndex: number) {
  if (simulation.value == null) return roundIndex + 1;
  return formatRoundName(
    simulation.value.durationType,
    simulation.value.roundUnit,
    simulation.value.roundLength,
    roundIndex,
    simulation.value.timeStart
  );
}
</script>

<style lang="scss">
.round-menu {
  padding-top: 0.5rem;

  &__pendingeffectstitle {
    background-color: transparent;
    display: flex;
    justify-content: space-between;
    margin-top: 0;
    border: none;
    border-radius: 3px;

    &:hover {
      color: var(--color-text);
    }

    &:focus-visible {
      outline: 2px solid var(--color-action);
      outline-offset: 1px;
    }
  }

  &__pendingeffectdate {
    font-size: 13px;
    color: var(--color-text-light);
  }

  &__actionicon {
    margin-right: 2px;
    margin-left: -3px;
    width: 1em;
    text-align: right;
    color: var(--color-text-light);
  }
}

.button-item.not-playable {
  border-color: var(--color-error);

  .button-item__text {
    text-decoration: line-through;
  }
}

.option_not-playable {
  text-decoration: line-through;
}
</style>
